import type { RAGEnv, Document, QueryRequest, QueryResponse } from '../types';
import { generateId } from '../core/utils';

/**
 * Enterprise security features for RAG systems
 * Ensures data stays in customer's Cloudflare account with strict security controls
 */
export class EnterpriseSecurityManager {
  private env: RAGEnv;
  private securityMode: 'strict' | 'relaxed';
  private auditLogger: AuditLogger;
  private accessController: AccessController;

  constructor(env: RAGEnv, securityMode: 'strict' | 'relaxed' = 'strict') {
    this.env = env;
    this.securityMode = securityMode;
    this.auditLogger = new AuditLogger(env);
    this.accessController = new AccessController(env, securityMode);
  }

  /**
   * Validate document before ingestion
   */
  async validateDocumentIngestion(document: Document, userId?: string): Promise<{
    allowed: boolean;
    reason?: string;
    sanitizedDocument?: Document;
  }> {
    try {
      // Check user permissions
      const userAllowed = await this.accessController.checkUserPermission(
        userId,
        'document:ingest',
        document.metadata.source
      );

      if (!userAllowed) {
        await this.auditLogger.logSecurityEvent({
          type: 'UNAUTHORIZED_ACCESS',
          userId,
          action: 'document:ingest',
          resource: document.id,
          timestamp: Date.now(),
          details: { source: document.metadata.source },
        });

        return {
          allowed: false,
          reason: 'Insufficient permissions for document ingestion',
        };
      }

      // Sanitize document content
      const sanitizedDocument = await this.sanitizeDocument(document);

      // Check for sensitive data
      const sensitiveDataCheck = await this.detectSensitiveData(sanitizedDocument.content);
      if (sensitiveDataCheck.hasSensitiveData && this.securityMode === 'strict') {
        await this.auditLogger.logSecurityEvent({
          type: 'SENSITIVE_DATA_DETECTED',
          userId,
          action: 'document:ingest',
          resource: document.id,
          timestamp: Date.now(),
          details: { 
            sensitiveDataTypes: sensitiveDataCheck.types,
            source: document.metadata.source,
          },
        });

        return {
          allowed: false,
          reason: `Sensitive data detected: ${sensitiveDataCheck.types.join(', ')}`,
        };
      }

      // Log successful validation
      await this.auditLogger.logSecurityEvent({
        type: 'DOCUMENT_VALIDATED',
        userId,
        action: 'document:ingest',
        resource: document.id,
        timestamp: Date.now(),
        details: { source: document.metadata.source },
      });

      return {
        allowed: true,
        sanitizedDocument,
      };
    } catch (error) {
      console.error('Document validation failed:', error);
      return {
        allowed: false,
        reason: 'Document validation failed',
      };
    }
  }

  /**
   * Validate query request
   */
  async validateQueryRequest(query: QueryRequest, userId?: string): Promise<{
    allowed: boolean;
    reason?: string;
    sanitizedQuery?: QueryRequest;
  }> {
    try {
      // Check user permissions for querying
      const userAllowed = await this.accessController.checkUserPermission(
        userId,
        'query:execute',
        query.namespace
      );

      if (!userAllowed) {
        await this.auditLogger.logSecurityEvent({
          type: 'UNAUTHORIZED_ACCESS',
          userId,
          action: 'query:execute',
          resource: query.query,
          timestamp: Date.now(),
          details: { namespace: query.namespace },
        });

        return {
          allowed: false,
          reason: 'Insufficient permissions for query execution',
        };
      }

      // Sanitize query
      const sanitizedQuery = await this.sanitizeQuery(query);

      // Check for malicious patterns
      const maliciousCheck = await this.detectMaliciousQuery(sanitizedQuery.query);
      if (maliciousCheck.isMalicious) {
        await this.auditLogger.logSecurityEvent({
          type: 'MALICIOUS_QUERY_DETECTED',
          userId,
          action: 'query:execute',
          resource: query.query,
          timestamp: Date.now(),
          details: { 
            maliciousPatterns: maliciousCheck.patterns,
            namespace: query.namespace,
          },
        });

        return {
          allowed: false,
          reason: `Malicious query patterns detected: ${maliciousCheck.patterns.join(', ')}`,
        };
      }

      // Log successful query validation
      await this.auditLogger.logSecurityEvent({
        type: 'QUERY_VALIDATED',
        userId,
        action: 'query:execute',
        resource: sanitizedQuery.query,
        timestamp: Date.now(),
        details: { namespace: query.namespace },
      });

      return {
        allowed: true,
        sanitizedQuery,
      };
    } catch (error) {
      console.error('Query validation failed:', error);
      return {
        allowed: false,
        reason: 'Query validation failed',
      };
    }
  }

  /**
   * Filter query response based on user permissions
   */
  async filterQueryResponse(response: QueryResponse, userId?: string): Promise<QueryResponse> {
    try {
      // Filter sources based on user access
      const filteredSources = [];
      for (const source of response.sources) {
        const hasAccess = await this.accessController.checkUserPermission(
          userId,
          'document:read',
          source.documentId
        );

        if (hasAccess) {
          filteredSources.push(source);
        } else {
          // Log access denied
          await this.auditLogger.logSecurityEvent({
            type: 'ACCESS_DENIED',
            userId,
            action: 'document:read',
            resource: source.documentId,
            timestamp: Date.now(),
          });
        }
      }

      // Redact sensitive information from response
      const redactedAnswer = await this.redactSensitiveInformation(response.answer, userId);

      return {
        ...response,
        answer: redactedAnswer,
        sources: filteredSources,
      };
    } catch (error) {
      console.error('Response filtering failed:', error);
      return response;
    }
  }

  /**
   * Encrypt document content at rest
   */
  async encryptDocument(document: Document): Promise<{
    encryptedContent: string;
    encryptionKey: string;
    metadata: any;
  }> {
    try {
      const key = await this.generateEncryptionKey();
      const encryptedContent = await this.encrypt(document.content, key);
      
      return {
        encryptedContent,
        encryptionKey: key,
        metadata: {
          ...document.metadata,
          encrypted: true,
          encryptionAlgorithm: 'AES-256-GCM',
        },
      };
    } catch (error) {
      throw new Error(`Document encryption failed: ${error}`);
    }
  }

  /**
   * Decrypt document content
   */
  async decryptDocument(
    encryptedContent: string,
    encryptionKey: string,
    userId?: string
  ): Promise<string> {
    try {
      // Log decryption attempt
      await this.auditLogger.logSecurityEvent({
        type: 'DOCUMENT_DECRYPTED',
        userId,
        action: 'document:decrypt',
        resource: 'encrypted_document',
        timestamp: Date.now(),
      });

      return await this.decrypt(encryptedContent, encryptionKey);
    } catch (error) {
      throw new Error(`Document decryption failed: ${error}`);
    }
  }

  /**
   * Generate secure encryption key
   */
  private async generateEncryptionKey(): Promise<string> {
    const key = await crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt']
    );

    const exported = await crypto.subtle.exportKey('raw', key);
    return Array.from(new Uint8Array(exported))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Encrypt content using AES-256-GCM
   */
  private async encrypt(content: string, keyHex: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    
    const keyBuffer = new Uint8Array(keyHex.match(/.{2}/g)!.map(byte => parseInt(byte, 16)));
    const key = await crypto.subtle.importKey('raw', keyBuffer, 'AES-GCM', false, ['encrypt']);
    
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
    
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    return Array.from(combined).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Decrypt content using AES-256-GCM
   */
  private async decrypt(encryptedHex: string, keyHex: string): Promise<string> {
    const encryptedBuffer = new Uint8Array(encryptedHex.match(/.{2}/g)!.map(byte => parseInt(byte, 16)));
    const keyBuffer = new Uint8Array(keyHex.match(/.{2}/g)!.map(byte => parseInt(byte, 16)));
    
    const key = await crypto.subtle.importKey('raw', keyBuffer, 'AES-GCM', false, ['decrypt']);
    
    const iv = encryptedBuffer.slice(0, 12);
    const data = encryptedBuffer.slice(12);
    
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
    return new TextDecoder().decode(decrypted);
  }

  /**
   * Sanitize document content
   */
  private async sanitizeDocument(document: Document): Promise<Document> {
    let sanitizedContent = document.content;
    
    // Remove potential script injections
    sanitizedContent = sanitizedContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    // Remove other potentially dangerous HTML tags
    sanitizedContent = sanitizedContent.replace(/<(iframe|object|embed|form)\b[^>]*>.*?<\/\1>/gi, '');
    
    // Sanitize URLs
    sanitizedContent = sanitizedContent.replace(/javascript:/gi, '');
    
    return {
      ...document,
      content: sanitizedContent,
    };
  }

  /**
   * Sanitize query
   */
  private async sanitizeQuery(query: QueryRequest): Promise<QueryRequest> {
    let sanitizedQuery = query.query;
    
    // Remove potential injection attempts
    sanitizedQuery = sanitizedQuery.replace(/['";]/g, '');
    
    // Limit query length
    if (sanitizedQuery.length > 1000) {
      sanitizedQuery = sanitizedQuery.substring(0, 1000);
    }
    
    return {
      ...query,
      query: sanitizedQuery,
    };
  }

  /**
   * Detect sensitive data in content
   */
  private async detectSensitiveData(content: string): Promise<{
    hasSensitiveData: boolean;
    types: string[];
  }> {
    const sensitivePatterns = [
      { type: 'SSN', pattern: /\b\d{3}-\d{2}-\d{4}\b/g },
      { type: 'Credit Card', pattern: /\b\d{4}[\s-]\d{4}[\s-]\d{4}[\s-]\d{4}\b/g },
      { type: 'Email', pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g },
      { type: 'Phone', pattern: /\b\d{3}-\d{3}-\d{4}\b/g },
      { type: 'API Key', pattern: /\b[A-Za-z0-9]{32,}\b/g },
    ];

    const detectedTypes: string[] = [];
    
    for (const { type, pattern } of sensitivePatterns) {
      if (pattern.test(content)) {
        detectedTypes.push(type);
      }
    }

    return {
      hasSensitiveData: detectedTypes.length > 0,
      types: detectedTypes,
    };
  }

  /**
   * Detect malicious query patterns
   */
  private async detectMaliciousQuery(query: string): Promise<{
    isMalicious: boolean;
    patterns: string[];
  }> {
    const maliciousPatterns = [
      { type: 'SQL Injection', pattern: /union\s+select|drop\s+table|delete\s+from/i },
      { type: 'Script Injection', pattern: /<script|javascript:/i },
      { type: 'Command Injection', pattern: /;\s*(rm|del|format|shutdown)/i },
    ];

    const detectedPatterns: string[] = [];
    
    for (const { type, pattern } of maliciousPatterns) {
      if (pattern.test(query)) {
        detectedPatterns.push(type);
      }
    }

    return {
      isMalicious: detectedPatterns.length > 0,
      patterns: detectedPatterns,
    };
  }

  /**
   * Redact sensitive information from response
   */
  private async redactSensitiveInformation(answer: string, userId?: string): Promise<string> {
    if (this.securityMode === 'relaxed') {
      return answer;
    }

    let redactedAnswer = answer;
    
    // Redact sensitive patterns
    redactedAnswer = redactedAnswer.replace(/\b\d{3}-\d{2}-\d{4}\b/g, '***-**-****');
    redactedAnswer = redactedAnswer.replace(/\b\d{4}[\s-]\d{4}[\s-]\d{4}[\s-]\d{4}\b/g, '****-****-****-****');
    
    return redactedAnswer;
  }
}

/**
 * Audit logging for security events
 */
class AuditLogger {
  private env: RAGEnv;

  constructor(env: RAGEnv) {
    this.env = env;
  }

  async logSecurityEvent(event: {
    type: string;
    userId?: string;
    action: string;
    resource: string;
    timestamp: number;
    details?: any;
  }): Promise<void> {
    try {
      const logEntry = {
        id: generateId(),
        ...event,
      };

      // Store in KV for audit trail
      const key = `audit:${event.timestamp}:${logEntry.id}`;
      await this.env.RAG_KV.put(key, JSON.stringify(logEntry), {
        expirationTtl: 365 * 24 * 60 * 60, // 1 year retention
      });

      // Also send to analytics if available
      if (this.env.ANALYTICS) {
        await this.env.ANALYTICS.writeDataPoint({
          blobs: [JSON.stringify(logEntry)],
          doubles: [event.timestamp],
          indexes: [event.type, event.action],
        });
      }

      console.log(`Audit event logged: ${event.type}`);
    } catch (error) {
      console.error('Failed to log audit event:', error);
    }
  }

  async getAuditTrail(
    userId?: string,
    startTime?: number,
    endTime?: number
  ): Promise<any[]> {
    // This would require a more sophisticated querying system
    // For now, return empty array
    console.warn('Audit trail querying not fully implemented');
    return [];
  }
}

/**
 * Access control management
 */
class AccessController {
  private env: RAGEnv;
  private securityMode: 'strict' | 'relaxed';

  constructor(env: RAGEnv, securityMode: 'strict' | 'relaxed') {
    this.env = env;
    this.securityMode = securityMode;
  }

  async checkUserPermission(
    userId: string | undefined,
    action: string,
    resource?: string
  ): Promise<boolean> {
    if (this.securityMode === 'relaxed') {
      return true;
    }

    if (!userId) {
      return false;
    }

    try {
      // Get user permissions from KV store
      const permissionsData = await this.env.RAG_KV.get(`permissions:${userId}`);
      if (!permissionsData) {
        return false;
      }

      const permissions = JSON.parse(permissionsData);
      
      // Check specific permission
      return permissions.includes(action) || permissions.includes('*');
    } catch (error) {
      console.error('Permission check failed:', error);
      return false;
    }
  }

  async grantPermission(userId: string, permission: string): Promise<void> {
    try {
      const permissionsData = await this.env.RAG_KV.get(`permissions:${userId}`);
      const permissions = permissionsData ? JSON.parse(permissionsData) : [];
      
      if (!permissions.includes(permission)) {
        permissions.push(permission);
        await this.env.RAG_KV.put(`permissions:${userId}`, JSON.stringify(permissions));
      }
    } catch (error) {
      console.error('Failed to grant permission:', error);
    }
  }

  async revokePermission(userId: string, permission: string): Promise<void> {
    try {
      const permissionsData = await this.env.RAG_KV.get(`permissions:${userId}`);
      if (!permissionsData) return;
      
      const permissions = JSON.parse(permissionsData);
      const filteredPermissions = permissions.filter((p: string) => p !== permission);
      
      await this.env.RAG_KV.put(`permissions:${userId}`, JSON.stringify(filteredPermissions));
    } catch (error) {
      console.error('Failed to revoke permission:', error);
    }
  }
}

/**
 * Create enterprise security manager
 */
export function createEnterpriseSecurityManager(
  env: RAGEnv,
  securityMode?: 'strict' | 'relaxed'
): EnterpriseSecurityManager {
  const mode = securityMode || (env.RAG_SECURITY_MODE as 'strict' | 'relaxed') || 'strict';
  return new EnterpriseSecurityManager(env, mode);
}